import { useAuth } from '@clerk/clerk-react'
import { useCallback, useRef, useState } from 'react'

import { createFetcher } from '@/services/api'
import type { FeaturebaseFeedbackConfig } from '../types/featurebase'
import { isE2ETest } from '../utils/testUtils'

const FEATUREBASE_SDK_URL = 'https://do.featurebase.app/js/sdk.js'
const FEATUREBASE_SDK_ID = 'featurebase-sdk'

interface FeaturebaseUserData {
  email: string
  name: string
  userId: string
}

/**
 * Hook to lazy-load the Featurebase feedback widget.
 *
 * The widget is NOT loaded automatically on mount. Instead, call the returned
 * `initialize` function when the user wants to interact with feedback (e.g., clicks a button).
 *
 * This prevents unnecessary script loading and reduces initial page load.
 */
export function useFeaturebaseWidget(userData?: FeaturebaseUserData) {
  const { getToken } = useAuth()
  const [isInitialized, setIsInitialized] = useState(false)
  const [isInitializing, setIsInitializing] = useState(false)
  const isInitializingRef = useRef(false)

  const loadScript = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (document.getElementById(FEATUREBASE_SDK_ID)) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.id = FEATUREBASE_SDK_ID
      script.src = FEATUREBASE_SDK_URL
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('[Featurebase] Failed to load SDK script'))
      document.head.appendChild(script)
    })
  }, [])

  const initialize = useCallback(async () => {
    if (isE2ETest()) {
      return
    }

    if (isInitialized) {
      return
    }

    if (isInitializingRef.current) {
      return
    }

    if (!userData?.email) {
      console.warn('[Featurebase] Cannot initialize widget without user email')
      return
    }

    const organization = import.meta.env.VITE_FEATUREBASE_ORG
    if (!organization) {
      console.error(
        '[Featurebase] Organization is not set. Please define VITE_FEATUREBASE_ORG in your environment variables.'
      )
      return
    }

    isInitializingRef.current = true
    setIsInitializing(true)

    try {
      // Fetch a signed JWT from the backend for secure Featurebase authentication
      const fetcher = createFetcher(getToken)
      const nameParam = encodeURIComponent(userData.name)
      const { token: featurebaseJwt } = await fetcher<{ token: string }>(
        `/integrations/featurebase-feedback-token?name=${nameParam}`
      )

      await loadScript()

      // Ensure the Featurebase queue function is available
      if (typeof window.Featurebase !== 'function') {
        const fb = (...args: unknown[]) => {
          fb.q = fb.q || []
          fb.q.push(args)
        }
        fb.q = [] as unknown[]
        window.Featurebase = fb as unknown as typeof window.Featurebase
      }

      // Identify the user with a signed JWT for secure authentication
      await new Promise<void>((resolve, reject) => {
        window.Featurebase!(
          'identify',
          {
            organization,
            featurebaseJwt,
          },
          (err) => {
            if (err) {
              reject(err)
              return
            }
            resolve()
          }
        )
      })

      await new Promise<void>((resolve, reject) => {
        const config: FeaturebaseFeedbackConfig = {
          organization,
          theme: 'light',
          featurebaseJwt,
          defaultBoard: '6980c631645e3f98e82628a8',
          locale: 'en',
        }

        const timeout = setTimeout(() => {
          reject(new Error('[Featurebase] Widget initialization timed out'))
        }, 10000)

        window.Featurebase!('initialize_feedback_widget', config, (err, result) => {
          if (err) {
            clearTimeout(timeout)
            reject(err)
            return
          }
          if (result?.action === 'widgetReady') {
            clearTimeout(timeout)
            resolve()
          }
        })
      })

      setIsInitialized(true)
    } catch (error) {
      console.error('[Featurebase] Failed to initialize widget:', error)
    } finally {
      isInitializingRef.current = false
      setIsInitializing(false)
    }
  }, [userData, isInitialized, loadScript, getToken])

  const open = useCallback(() => {
    window.postMessage({
      target: 'FeaturebaseWidget',
      data: { action: 'openFeedbackWidget' },
    })
  }, [])

  return {
    initialize,
    open,
    isInitialized,
    isInitializing,
  }
}
